import { CommonModule, DatePipe } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { IonicModule } from '@ionic/angular';
import { AriaHiddenDirective } from 'src/app/shared/directives/aria-hidden.directive';
import { FormattedSummaryDatesAndTimesDetail } from 'src/app/shared/models/dates-and-times.model';
import { JobDetail } from 'src/app/shared/models/gql/job-detail.model';
import { RemoveTimezoneFromDatePipe } from 'src/app/shared/pipes/remove-timezone-from-date.pipe';
import { UtilityService } from 'src/app/shared/services/utility.service';

/**
 * The `View dates and times summary` component should be used to display a summary of frequency, date and time information for a posted job.
 */
@Component({
  standalone: true,
  selector: 'app-view-dates-and-times-summary',
  templateUrl: './view-dates-and-times-summary.component.html',
  styleUrls: ['./view-dates-and-times-summary.component.scss'],
  imports: [CommonModule, DatePipe, IonicModule, AriaHiddenDirective],
})
export class ViewDatesAndTimesSummaryComponent implements OnInit {
  /** Job detail to display */
  @Input() job: JobDetail;

  /** Whether the view is inline or not */
  @Input() isInlineView = false;

  // Job dates and times detail formatted for display
  formattedJobData: FormattedSummaryDatesAndTimesDetail;

  // DatePipe for formatting date.
  private datepipe: DatePipe = new DatePipe('en-AU');

  // RemoveTimezoneFromDatePipe for removing timezone from date.
  private removeTimezoneFromDate: RemoveTimezoneFromDatePipe = new RemoveTimezoneFromDatePipe();

  constructor(private utilityService: UtilityService) {}

  ngOnInit(): void {
    if (this.job) {
      if (this.job.frequency === 'once_off' || this.job.sessionFrequency === 'once_off') {
        this.formatJobData(this.job, '', true);
      } else {
        const jobStartDate = this.getStartDateString(this.job?.timestamps?.startTime);
        this.formatJobData(this.job, jobStartDate, false);
      }
    }
  }

  /**
   * Reformatting job frequency, dates and times data to be displayed in html.
   *
   * @param frequencyDatesAndTimes - Frequency dates and time information from job detail.
   * @param startDate - Contains formatted job start date or an empty string.
   * @param isOneOffJob - Contains true if job is a one off.
   */
  private formatJobData(frequencyDatesAndTimes: JobDetail, startDate: string, isOneOffJob: boolean): void {
    const {
      sessionFrequency,
      frequency,
      supportSessionsPerPeriod,
      estimatedHoursPerPeriod,
      estimatedHoursPerWeek,
      supportSessionsPerWeek,
    } = frequencyDatesAndTimes;

    if (isOneOffJob) {
      this.formattedJobData = {
        frequency: 'One-off',
        sessions: '',
        hours: this.getHoursString(estimatedHoursPerPeriod || estimatedHoursPerWeek),
        jobStartDetail: '',
        isOneOffJob,
      };
    } else {
      let frequencyString = '';
      let hoursString = '';
      let sessionsString = '';
      if (
        supportSessionsPerPeriod !== null &&
        estimatedHoursPerPeriod !== null &&
        (supportSessionsPerPeriod > 0 || estimatedHoursPerPeriod > 0)
      ) {
        // sessionFrequency, supportSessionsPerPeriod and estimatedHoursPerPeriod (allows for fortnightly, monthly etc)
        frequencyString = this.getFrequencyString(sessionFrequency);
        hoursString = this.getHoursString(estimatedHoursPerPeriod);
        sessionsString = this.getSessionsString(supportSessionsPerPeriod);
      } else {
        // frequency, supportSessionsPerWeek and estimatedHoursPerWeek is pre-frequency option feature
        frequencyString = this.getFrequencyString(frequency);
        hoursString = this.getHoursString(estimatedHoursPerWeek);
        sessionsString = this.getSessionsString(supportSessionsPerWeek);
      }

      const jobStartDetail = startDate.length ? `Starting from ${startDate}` : 'Starting as soon as possible';

      this.formattedJobData = {
        frequency: frequencyString,
        sessions: sessionsString,
        hours: hoursString,
        jobStartDetail,
        isOneOffJob,
      };
    }
  }

  /**
   * Format job frequency into a string.
   *
   * @param frequency - job frequency
   * @returns formatted frequency
   */
  private getFrequencyString(frequency: string): string {
    const formattedFrequency = frequency && frequency !== 'ongoing' ? `${frequency}ly` : 'weekly';
    return this.utilityService.capitalize(formattedFrequency);
  }

  /**
   * Format job sessions into a string.
   *
   * @param supportSessions - job support sessions
   * @returns reformatted job sessions
   */
  private getSessionsString(supportSessions: number): string {
    if (supportSessions > 1) {
      return `${supportSessions} sessions`;
    }
    return `${supportSessions} session`;
  }

  /**
   * Format job hours into a string.
   *
   * @param estimatedHours - job hours
   * @returns reformatted job hours
   */
  private getHoursString(estimatedHours: number): string {
    if (estimatedHours > 1) {
      return `${estimatedHours} hours`;
    }
    return `${estimatedHours} hour`;
  }

  /**
   * Format job start date into a string.
   *
   * @param startDate - The startTime of a posted job.
   * @returns formatted start date
   */
  private getStartDateString(startDate: string | null): string {
    if (startDate) {
      const formattedDate = this.datepipe.transform(this.removeTimezoneFromDate.transform(startDate), 'dd MMMM y');
      if (formattedDate.charAt(0) === '0') {
        return formattedDate.slice(1);
      }
      return formattedDate;
    }
    return '';
  }
}
